import type { z } from "zod";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** `/users/:player_ref` becomes `{ player_ref: string }`. */
export type PathParams<Path extends string> = Path extends `${string}:${infer Param}/${infer Rest}`
  ? Record<Param | keyof PathParams<`/${Rest}`>, string>
  : Path extends `${string}:${infer Param}`
    ? Record<Param, string>
    : object;

/**
 * One Explorer API operation, declared with the zod schemas of `@strust/contracts`. The schemas
 * are the single source of truth: the client infers request and response types from them and
 * validates every response at the edge.
 */
export interface Endpoint<
  Path extends string = string,
  Response extends z.ZodType = z.ZodType,
  Body extends z.ZodType | undefined = z.ZodType | undefined,
  Query extends z.ZodType | undefined = z.ZodType | undefined,
> {
  readonly method: HttpMethod;
  readonly path: Path;
  readonly response: Response;
  readonly body?: Body;
  readonly query?: Query;
}

export function defineEndpoint<
  const Path extends string,
  Response extends z.ZodType,
  Body extends z.ZodType | undefined = undefined,
  Query extends z.ZodType | undefined = undefined,
>(endpoint: Endpoint<Path, Response, Body, Query>): Endpoint<Path, Response, Body, Query> {
  return endpoint;
}

type Declared<S> = Exclude<S, undefined>;

/** Arguments of a call: only what the endpoint declares is accepted (and required). */
export type CallInput<E extends Endpoint> = (keyof PathParams<E["path"]> extends never
  ? { readonly params?: undefined }
  : { readonly params: PathParams<E["path"]> }) &
  ([Declared<E["body"]>] extends [never]
    ? { readonly body?: undefined }
    : { readonly body: z.input<Declared<E["body"]>> }) &
  ([Declared<E["query"]>] extends [never]
    ? { readonly query?: undefined }
    : { readonly query?: z.input<Declared<E["query"]>> }) & { readonly signal?: AbortSignal };
